// Configuración base de la API
// Usa variable de entorno con múltiples fallbacks para compatibilidad
const rawApiUrl =
  import.meta.env.VITE_API_BE ||
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_API_URL

// Normalizar la URL: quitar slash final y asegurar que termine con /api
const normalizeApiUrl = (url) => {
  if (!url) return null
  const normalized = url.replace(/\/+$/, '')
  return normalized.endsWith('/api') ? normalized : `${normalized}/api`
}

const API_BASE_URL = normalizeApiUrl(rawApiUrl)

// Prefijos de endpoints que EXIGEN x-company-tenant-id (scope por empresa).
// Sin empresa activa, un GET a estos devuelve 400 TENANT_ID_REQUIRED; el guard
// de api.js los corta antes de salir a la red. Lista positiva y específica: un
// prefijo que falte solo deja el comportamiento actual (nunca bloquea un
// endpoint no-tenant). NO incluir '/companies' ni '/subscription' a secas
// (su listado/planes no llevan tenant); usar solo las subrutas tenantizadas.
const TENANT_SCOPED_PREFIXES = [
  '/appointments',
  '/clients',
  '/services',
  '/service-orders',
  '/promotions',
  '/reports',
  '/notifications',
  '/audit-logs',
  '/media',
  '/google-calendar',
  '/workers-status',
  '/company-availability',
  '/companies/message-templates',
  '/companies/features',
  '/companies/public-booking',
  '/subscription/effective',
  '/promo-codes/company',
  '/support-tickets'
]

/** Clave en localStorage para el refresh token (debe coincidir con auth store). */
export const AUTH_REFRESH_TOKEN_KEY = 'auth_refresh_token'

// Callback para manejar sesión expirada (se configura desde el store de auth)
let onSessionExpired = null

/** Tras renovar el access token, sincroniza Pinia (se configura desde auth). */
let onAccessTokenRefreshed = null

// Callback para manejar errores de conexión (se configura desde App.vue)
let onConnectionError = null

// Callback para manejar suscripción expirada (upgrade_required en cualquier respuesta)
let onUpgradeRequired = null

/**
 * Configura el callback que se ejecuta cuando cualquier endpoint devuelve upgrade_required: true
 * @param {Function} callback - recibe el body completo de la respuesta (object con message, feature, requiredPlan, currentPlan, upgradeUrl, etc.)
 */
export function setUpgradeRequiredHandler(callback) {
  onUpgradeRequired = callback
}

/**
 * Configura el callback que se ejecuta cuando la sesión expira
 * @param {Function} callback
 */
export function setSessionExpiredHandler(callback) {
  onSessionExpired = callback
}

/**
 * Callback con el nuevo access token tras un refresh exitoso (sincronizar token en Pinia).
 * @param {Function} callback
 */
export function setAccessTokenRefreshCallback(callback) {
  onAccessTokenRefreshed = callback
}

/**
 * Configura el callback que se ejecuta cuando hay un error de conexión
 * @param {Function} callback
 */
export function setConnectionErrorHandler(callback) {
  onConnectionError = callback
}

const CONNECTION_TIMEOUT = 15000

/** Rutas donde un 401 no debe disparar intento de refresh (login/registro/refresh). */
function isAuthExemptFromRefresh(url) {
  if (!url || typeof url !== 'string') return true
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/register') ||
    url.includes('/auth/refresh')
  )
}

function isPublicBookingUrl(url) {
  return typeof url === 'string' && url.includes('/public/booking/')
}

/**
 * Modo suplantación (solo lectura): mientras esté activo, bloqueamos toda
 * mutación EN EL CLIENTE antes de salir a la red — mejor UX que esperar el 403
 * del backend (que igual la bloquea). Se exceptúan los endpoints de auth.
 */
function isImpersonationActive() {
  return !!localStorage.getItem('impersonation_meta')
}

function impersonationReadOnlyError() {
  const err = new Error('Modo suplantación (solo lectura): no podés modificar datos.')
  err.status = 403
  err.impersonationReadOnly = true
  return err
}

/** Mutex: una sola renovación concurrente; el resto espera la misma promesa. */
let refreshPromise = null

/** Mensaje interno cuando ya se mostró el modal de sesión; no mostrar toast rojo duplicado. */
export const SESSION_EXPIRED_ERROR_MESSAGE = 'SESSION_EXPIRED'

/** Error HTTP con el detalle del primer campo que falló (errors[].msg de express-validator o errors[].message de Sequelize). */
function buildHttpError(response, data) {
  const firstFieldError = Array.isArray(data?.errors) && data.errors.length > 0 ? data.errors[0] : null
  const firstFieldMsg = firstFieldError?.msg || firstFieldError?.message || null
  const error = new Error(firstFieldMsg || data?.message || data?.error || `Error ${response.status}`)
  error.status = response.status
  error.data = data
  if (firstFieldError) error.field = firstFieldError.field || firstFieldError.param || firstFieldError.path || null
  return error
}

/**
 * @param {unknown} err
 * @returns {boolean}
 */
export function isSessionExpiredError(err) {
  return (
    err != null &&
    typeof err === 'object' &&
    (err.sessionExpired === true || err.message === SESSION_EXPIRED_ERROR_MESSAGE)
  )
}

/**
 * Único punto que decide si un 401 ya recibido es "fin de sesión real". Si
 * vino después de un refresh que falló por un motivo transitorio (no por
 * vencimiento real del token), NO se trata como sesión expirada — el caller
 * lo deja seguir como cualquier otro error de la request.
 * @param {Response} response - puede traer `refreshFailureReason` seteado por fetchWithAuthRetry
 * @param {any} data
 * @returns {Error|null}
 */
function buildSessionExpiredErrorIfApplicable(response, data) {
  if (response.status !== 401) return null
  const isLoginRequest = response.url.includes('/auth/login')
  const isRegisterRequest = response.url.includes('/auth/register')
  if (isLoginRequest || isRegisterRequest || isPublicBookingUrl(response.url)) return null
  if (response.refreshFailureReason === 'transient') return null
  if (!onSessionExpired) return null

  onSessionExpired()
  const err = new Error(SESSION_EXPIRED_ERROR_MESSAGE)
  err.status = 401
  err.data = data
  err.sessionExpired = true
  return err
}

/**
 * Extrae access y refresh del JSON típico del backend (data anidado o plano).
 */
function parseTokensFromRefreshPayload(data) {
  const d = data?.data != null && typeof data.data === 'object' ? data.data : data
  const newAccess =
    d?.token ??
    d?.access_token ??
    d?.accessToken ??
    data?.token ??
    data?.access_token ??
    data?.accessToken
  const newRefresh = d?.refresh_token ?? d?.refreshToken ?? data?.refresh_token ?? data?.refreshToken
  return { newAccess, newRefresh }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Backoff corto para fallos transitorios del refresh (500, timeout, error de red) — no para 401 real ni 429. */
const TRANSIENT_RETRY_DELAYS_MS = [1000, 2000]

async function attemptRefreshRequest(apiInstance, rt) {
  const url = `${apiInstance.baseURL}/auth/refresh`
  const response = await apiInstance.fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // Documentación API: body solo { refreshToken }
    body: JSON.stringify({ refreshToken: rt }),
  })
  const data = await response.json().catch(() => ({}))
  return { response, data }
}

/**
 * Clasifica por qué falló /auth/refresh. 'expired' = la sesión realmente
 * terminó (token inválido/vencido, o el usuario ya no es válido) — hay que
 * limpiar todo. 'rate_limited'/'transient' = problema de infraestructura, la
 * sesión sigue viva, nunca se borra el refresh token por esto.
 * @returns {'expired'|'rate_limited'|'transient'}
 */
function classifyRefreshFailure(response, data) {
  if (response.status === 429) return 'rate_limited'
  if (data?.code === 'REFRESH_TOKEN_INVALID' || data?.code === 'REFRESH_USER_INACTIVE') return 'expired'
  if (response.status === 401) return 'expired' // compat con backends sin `code` todavía
  return 'transient' // 500, REFRESH_INTERNAL_ERROR, etc.
}

async function doRefreshAccessToken(apiInstance) {
  const rt = localStorage.getItem(AUTH_REFRESH_TOKEN_KEY)
  if (!rt || !apiInstance?.baseURL) return { ok: false, reason: 'expired' }

  const maxAttempts = TRANSIENT_RETRY_DELAYS_MS.length + 1 // intento inicial + reintentos
  let rateLimitRetried = false

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    let response, data
    try {
      ;({ response, data } = await attemptRefreshRequest(apiInstance, rt))
    } catch {
      // Error de red/timeout del propio fetch: mismo tratamiento que un fallo transitorio.
      if (attempt < maxAttempts - 1) {
        await sleep(TRANSIENT_RETRY_DELAYS_MS[attempt])
        continue
      }
      return { ok: false, reason: 'transient' }
    }

    if (response.ok) {
      const { newAccess, newRefresh } = parseTokensFromRefreshPayload(data)
      if (!newAccess) return { ok: false, reason: 'transient' } // 2xx sin token: respuesta no confiable, no matar la sesión

      localStorage.setItem('auth_token', newAccess)
      if (newRefresh) {
        localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, newRefresh)
      }
      if (typeof onAccessTokenRefreshed === 'function') {
        onAccessTokenRefreshed(newAccess)
      }
      return { ok: true }
    }

    const reason = classifyRefreshFailure(response, data)

    if (reason === 'expired') {
      localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY)
      return { ok: false, reason: 'expired' }
    }

    if (reason === 'rate_limited') {
      // No reintentar agresivo contra un límite ya agotado: una sola espera más larga.
      if (!rateLimitRetried) {
        rateLimitRetried = true
        const retryAfterSeconds = Number(response.headers?.get?.('Retry-After'))
        const waitMs = Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 3000
        await sleep(waitMs)
        continue
      }
      return { ok: false, reason: 'transient' }
    }

    // reason === 'transient'
    if (attempt < maxAttempts - 1) {
      await sleep(TRANSIENT_RETRY_DELAYS_MS[attempt])
      continue
    }
    return { ok: false, reason: 'transient' }
  }

  return { ok: false, reason: 'transient' }
}

/**
 * Intenta renovar el access token una vez (con reintentos internos ante
 * fallos transitorios); peticiones concurrentes comparten el mismo intento.
 * Resuelve `{ ok: true }` o `{ ok: false, reason: 'expired'|'transient' }`.
 * @param {ApiService} apiInstance
 */
function refreshAccessTokenOnce(apiInstance) {
  if (!refreshPromise) {
    refreshPromise = doRefreshAccessToken(apiInstance).finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

/**
 * Servicio de API para hacer peticiones al backend
 */
class ApiService {
  constructor(baseURL) {
    this.baseURL = baseURL
  }

  /**
   * Obtiene los headers por defecto para las peticiones
   * Cuando hay empresa activa con tenant_id, añade x-company-tenant-id para scope de citas, clientes, servicios, reportes y usuarios
   * @param {boolean} includeAuth
   * @param {string} [tenantOverride] - tenant_id explícito (empresa distinta a la activa); si se omite, usa resolveTenantId()
   */
  getHeaders(includeAuth = false, tenantOverride) {
    const headers = {
      'Content-Type': 'application/json',
    }

    if (includeAuth) {
      const token = localStorage.getItem('auth_token')
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
      const tenantId = tenantOverride !== undefined ? tenantOverride : this.resolveTenantId()
      if (tenantId) {
        headers['x-company-tenant-id'] = tenantId
      }
    }

    return headers
  }

  /**
   * Resuelve el tenant_id de la empresa activa desde localStorage (misma lógica
   * que el header x-company-tenant-id). Devuelve null si no hay empresa activa.
   */
  resolveTenantId() {
    const activeCompany = localStorage.getItem('active_company')
    if (!activeCompany) return null
    try {
      const company = JSON.parse(activeCompany)
      let tenantId = company.tenant_id
      if (!tenantId && company.id) {
        const list = localStorage.getItem('user_companies')
        if (list) {
          const companies = JSON.parse(list)
          const found = Array.isArray(companies) ? companies.find((c) => c.id === company.id) : null
          if (found?.tenant_id) tenantId = found.tenant_id
        }
      }
      return tenantId || null
    } catch (_) {
      return null
    }
  }

  /** True si el endpoint requiere x-company-tenant-id (ver TENANT_SCOPED_PREFIXES). */
  isTenantScoped(endpoint) {
    const path = String(endpoint).split('?')[0]
    return TENANT_SCOPED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))
  }

  /**
   * Respuesta benigna para un GET tenantizado disparado sin empresa activa:
   * corta antes de la red para no producir 400 TENANT_ID_REQUIRED. Mantiene el
   * envelope estándar con data vacía para que los callers rendericen vacío.
   */
  noActiveCompanyResult() {
    return { success: false, code: 'NO_ACTIVE_COMPANY', message: '', data: [] }
  }

  /**
   * Fetch con timeout - si no responde en el tiempo límite, lanza error de conexión
   */
  async fetchWithTimeout(url, options) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), CONNECTION_TIMEOUT)
    const externalSignal = options?.signal
    const abortFromExternal = () => controller.abort()

    if (externalSignal?.aborted) {
      clearTimeout(timeoutId)
      throw new DOMException('The operation was aborted.', 'AbortError')
    }
    externalSignal?.addEventListener('abort', abortFromExternal, { once: true })

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
      externalSignal?.removeEventListener('abort', abortFromExternal)
      return response
    } catch (err) {
      clearTimeout(timeoutId)
      if (err.name === 'AbortError') {
        externalSignal?.removeEventListener('abort', abortFromExternal)
        throw err
      }
      if (onConnectionError) onConnectionError()
      if (err.name === 'AbortError') {
        throw new Error('Error de conexión, intente nuevamente')
      }
      throw new Error('Error de conexión, intente nuevamente')
    }
  }

  /**
   * Si 401 con auth y no es ruta de auth, intenta refresh y reintenta la misma petición una vez.
   * @param {string} [tenantOverride] - se reenvía al rearmar headers post-refresh (ver getHeaders)
   */
  async fetchWithAuthRetry(url, options, includeAuth, tenantOverride) {
    let response = await this.fetchWithTimeout(url, options)
    if (response.status === 401 && includeAuth && !isAuthExemptFromRefresh(url)) {
      const refreshResult = await refreshAccessTokenOnce(this)
      if (refreshResult.ok) {
        response = await this.fetchWithTimeout(url, {
          ...options,
          headers: this.getHeaders(includeAuth, tenantOverride),
        })
      } else {
        // Que handleResponse/postWithStatus/putWithStatus sepan por qué falló el
        // refresh antes de decidir si esto es "fin de sesión" o un error normal.
        response.refreshFailureReason = refreshResult.reason
      }
    }
    return response
  }

  /**
   * Maneja la respuesta de la API
   * @param {Response} response
   * @param {{ silentUpgrade?: boolean }} [options]
   */
  async handleResponse(response, options = {}) {
    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      const sessionExpiredError = buildSessionExpiredErrorIfApplicable(response, data)
      if (sessionExpiredError) throw sessionExpiredError

      // Dos formas conviven: express-validator manda `msg`/`param`, y los controllers que
      // rebotan un error de Sequelize mandan `message`/`field`. Antes solo se leía `msg`, así
      // que un error de modelo caía siempre en el genérico "Error de validación".
      const firstFieldError = Array.isArray(data.errors) && data.errors.length > 0 ? data.errors[0] : null
      const firstFieldMsg = firstFieldError?.msg || firstFieldError?.message || null
      // El detalle del campo gana sobre `data.message`: "Error de validación" no le dice nada a nadie.
      const error = new Error(firstFieldMsg || data.message || data.error || `Error ${response.status}`)
      error.status = response.status
      error.data = data
      // Lo consume el formulario para hacer foco en el campo que falló.
      if (firstFieldError) error.field = firstFieldError.field || firstFieldError.param || null

      if (data?.upgrade_required === true) {
        error.upgradeRequired = true
        // El cliente público del calendario compartido no tiene sesión ni plan propio —
        // este modal es para el dueño del negocio, nunca para quien está reservando.
        if (!options.silentUpgrade && !isPublicBookingUrl(response.url) && onUpgradeRequired) {
          onUpgradeRequired(data)
        }
      }

      throw error
    }

    // Respuesta 2xx pero con upgrade_required en el body (ej: suscripción expirada con 200).
    // Este caso es un estado global de cuenta — siempre abre el modal sin importar silentUpgrade,
    // salvo en el calendario público (ver comentario arriba).
    if (data?.success === false && data?.upgrade_required === true) {
      if (!isPublicBookingUrl(response.url) && onUpgradeRequired) {
        onUpgradeRequired(data)
      }
      const error = new Error(data.message || 'Tu suscripción ha expirado')
      error.status = 402
      error.data = data
      error.upgradeRequired = true
      throw error
    }

    return data
  }

  /**
   * Realiza una petición POST
   * @param {{ tenantId?: string }} [options] - tenantId explícito para operar sobre otra empresa (ver `put()`)
   */
  async post(endpoint, body, includeAuth = false, options = {}) {
    if (isImpersonationActive() && !endpoint.startsWith('/auth/')) throw impersonationReadOnlyError()
    const url = `${this.baseURL}${endpoint}`
    const response = await this.fetchWithAuthRetry(
      url,
      {
        method: 'POST',
        headers: this.getHeaders(includeAuth, options.tenantId),
        body: JSON.stringify(body),
      },
      includeAuth,
      options.tenantId
    )

    return this.handleResponse(response)
  }

  /**
   * POST que devuelve cuerpo parseado y código HTTP (p. ej. comprobar 201 Created).
   * @param {{ tenantId?: string }} [options] - ver `put()`
   */
  async postWithStatus(endpoint, body, includeAuth = false, options = {}) {
    if (isImpersonationActive() && !endpoint.startsWith('/auth/')) throw impersonationReadOnlyError()
    const url = `${this.baseURL}${endpoint}`
    const response = await this.fetchWithAuthRetry(
      url,
      {
        method: 'POST',
        headers: this.getHeaders(includeAuth, options.tenantId),
        body: JSON.stringify(body),
      },
      includeAuth,
      options.tenantId
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      const sessionExpiredError = buildSessionExpiredErrorIfApplicable(response, data)
      if (sessionExpiredError) throw sessionExpiredError
      // Antes solo usaba data.message: crear o editar una cita mostraba "Error de validación" sin decir qué campo.
      throw buildHttpError(response, data)
    }

    return { data, status: response.status }
  }

  /**
   * Realiza una petición GET
   * @param {{ signal?: AbortSignal, silentUpgrade?: boolean, tenantId?: string }} [options] - `tenantId`
   * explícito para leer de OTRA empresa distinta a la activa (ver `put()`), sin tocar localStorage.
   */
  async get(endpoint, includeAuth = true, options = {}) {
    // Sin empresa activa (ni override) no se piden recursos tenantizados (evita 400 TENANT_ID_REQUIRED).
    if (includeAuth && this.isTenantScoped(endpoint) && !(options.tenantId || this.resolveTenantId())) {
      return this.noActiveCompanyResult()
    }
    const url = `${this.baseURL}${endpoint}`
    const response = await this.fetchWithAuthRetry(
      url,
      {
        method: 'GET',
        headers: this.getHeaders(includeAuth, options.tenantId),
        signal: options.signal,
      },
      includeAuth,
      options.tenantId
    )

    return this.handleResponse(response, { silentUpgrade: options.silentUpgrade ?? true })
  }

  /**
   * Realiza una petición PUT
   * @param {{ tenantId?: string }} [options] - tenantId explícito para operar sobre una empresa
   * distinta a la activa (ej. onboarding actualizando otra empresa del usuario), sin tocar
   * localStorage.active_company. Sin esto, usa resolveTenantId() (empresa activa) como siempre.
   */
  async put(endpoint, body, includeAuth = true, options = {}) {
    if (isImpersonationActive() && !endpoint.startsWith('/auth/')) throw impersonationReadOnlyError()
    const url = `${this.baseURL}${endpoint}`
    const response = await this.fetchWithAuthRetry(
      url,
      {
        method: 'PUT',
        headers: this.getHeaders(includeAuth, options.tenantId),
        body: JSON.stringify(body),
      },
      includeAuth,
      options.tenantId
    )

    return this.handleResponse(response)
  }

  /**
   * PUT que devuelve cuerpo parseado y código HTTP (mismo patrón que postWithStatus).
   * @param {{ tenantId?: string }} [options] - ver `put()`
   */
  async putWithStatus(endpoint, body, includeAuth = true, options = {}) {
    if (isImpersonationActive() && !endpoint.startsWith('/auth/')) throw impersonationReadOnlyError()
    const url = `${this.baseURL}${endpoint}`
    const response = await this.fetchWithAuthRetry(
      url,
      {
        method: 'PUT',
        headers: this.getHeaders(includeAuth, options.tenantId),
        body: JSON.stringify(body),
      },
      includeAuth,
      options.tenantId
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      const sessionExpiredError = buildSessionExpiredErrorIfApplicable(response, data)
      if (sessionExpiredError) throw sessionExpiredError
      // Antes solo usaba data.message: crear o editar una cita mostraba "Error de validación" sin decir qué campo.
      throw buildHttpError(response, data)
    }

    return { data, status: response.status }
  }

  /**
   * Realiza una petición DELETE. `body` es opcional (ej. confirmar password
   * antes de una acción destructiva) y solo se serializa si se provee.
   */
  async delete(endpoint, includeAuth = true, body) {
    if (isImpersonationActive() && !endpoint.startsWith('/auth/')) throw impersonationReadOnlyError()
    const url = `${this.baseURL}${endpoint}`
    const response = await this.fetchWithAuthRetry(
      url,
      {
        method: 'DELETE',
        headers: this.getHeaders(includeAuth),
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      },
      includeAuth
    )

    return this.handleResponse(response)
  }

  /*Realiza una petición PATCH*/
  async patch(endpoint, body, includeAuth = true) {
    if (isImpersonationActive() && !endpoint.startsWith('/auth/')) throw impersonationReadOnlyError()
    const url = `${this.baseURL}${endpoint}`
    const response = await this.fetchWithAuthRetry(
      url,
      {
        method: 'PATCH',
        headers: this.getHeaders(includeAuth),
        body: JSON.stringify(body),
      },
      includeAuth
    )

    return this.handleResponse(response)
  }
}

// Exportar instancia única del servicio
export const api = new ApiService(API_BASE_URL)

// Exportar también la URL base por si se necesita
export { API_BASE_URL }
